import { featureFlags } from 'config/feature-flags'
import PLAN_TYPE, { QUOTA_MODE } from 'types/utils'
import hasProFeature, { getPlanLicense as getPlanLicenseInternal } from 'utils/plan/tier'

// plan_tier 상등급 값 — tier.js 는 의존성 zero 규약이라 상수를 공유하지 않고 각자 비교한다.
export const PLAN_TIER_PROFESSIONAL = 'PROFESSIONAL'

// TOTP 게이팅 헬퍼 — featureFlags.totpAuthenticator 직접 참조 지점들의 단일 치환 지점.
// 요금제 4종 개편(plan_tier)에서 등급별로 갈리는 두 부류를 분리한다:
//
//  - create 계열(isTotpCreateAllowed): 생성 진입점(필드 타입 드롭다운·템플릿·가져오기)과
//    인증 코드 탭 접근. flag AND tier — Phase 3 부터 글로벌향·PAID·Basic 에서 차단된다.
//  - view 계열(isTotpViewAllowed): 기존 데이터 렌더(필드·카드)·내보내기.
//    flag 만 검사 — 킬스위치 역할은 유지하되, 다운그레이드 고객의 기존 TOTP 조회를 보존한다.
//
// flag 는 호출 시점에 읽는다 — featureFlags 를 런타임에 변경하는 기존 테스트 패턴과 호환.
export const isTotpCreateAllowed = (): boolean => featureFlags.totpAuthenticator && hasProFeature()

export const isTotpViewAllowed = (): boolean => featureFlags.totpAuthenticator

// Basic(글로벌향·PAID)에서 totp 아이템을 보유한 레코드의
// 공유 화면 안내(record.totp.shareNotice) 표시 판정 — **차단이 아니다.**
// 서버에는 대응 가드가 없다(함수째 삭제, 회귀 테스트로 고정).
// 2026-08-25 에 이 판정으로 공유 추가를 493 으로 막았다가 08-26 철회했다: 공유는 같은
// 워크스페이스 안에서만 되므로 수신자도 같은 등급이고, 그가 새로 얻는 건 Basic 에 이미
// 허용된 조회뿐이다 — 막아도 지켜지는 매출이 없는데 인수인계가 교착되고, 내보내기로
// 우회되면 감사 로그만 사라진다. 되살리지 말 것.
// ⚠️ 2026-08-24~08-26 에는 같은 판정으로 **레코드 편집 전체**를 막았는데 폐지됐다 —
// 상한 초과 첨부가 함께 있는 레코드에서 "초과 파일을 삭제하라" 는 안내를 따를 수 없는
// 교착이 생겼기 때문(편집 진입이 막혀 삭제 불가). 되살리지 말 것. 기존 totp 값 보호는
// 필드 단위 disabled(records/components/helper.js canEditTotp)가 담당한다.
//
// flag(킬스위치)가 아니라 tier 만 본다 — 킬스위치 OFF 가 KR 까지 얼리면 안 되기 때문.
const isBasicWithTotpItem = (record: { items?: Array<{ type?: string }> } | null): boolean =>
  !hasProFeature() && Boolean(record?.items?.some((item) => item?.type === 'totp'))

export const isTotpRecordShareNoticeNeeded = isBasicWithTotpItem

// totp **값 수정 불가** 안내 판정 (상세 카드 상단). 공유 안내와 조건이 겹치지만 정책이
// 달라 이름을 분리한다 — 이 계열 정책은 실제로 두 번 갈렸다(편집 전체 차단 도입→폐지,
// 공유 차단 도입→철회). 한쪽이 또 바뀔 때 다른 쪽을 건드리지 않으려면 호출부별 이름이 낫다.
//
// **필드 disabled 와 같은 판정을 쓴다**(`isTotpCreateAllowed` = flag AND tier) — 안내는
// 실제로 잠긴 것만 설명해야 한다. tier 만 보면 킬스위치 OFF + 글로벌향 Basic 에서 필드는
// 편집 가능한데(TotpFieldEdit 자체가 렌더되지 않아 disabled 도 사라진다) 안내는 "수정
// 불가" 라고 말하는 거짓이 된다 (2026-08-26 검토). KR 은 hasProFeature 가 이미 true 라
// flag 항을 더해도 동작이 바뀌지 않는다 — "flag 를 보면 KR 에 문구가 뜬다" 는 옛 주석의
// 우려는 성립하지 않았다.
export const isTotpValueReadOnly = (record: { items?: Array<{ type?: string }> } | null): boolean =>
  // ① totp 필드가 렌더되는 상태여야 한다(flag ON) — flag OFF 면 TotpFieldEdit 대신 일반
  //    보안 필드가 나와 값이 자유롭게 편집되므로 "수정 불가" 는 거짓이다.
  // ② 그 필드가 실제로 disabled 여야 한다(= 생성 불가 등급).
  isTotpViewAllowed() && !isTotpCreateAllowed() && isBasicWithTotpItem(record)

// 플랜 파일 상한(서버 산출 max_file_size_bytes) — 안내(notice)용.
// Attachment/helper 의 getAttachmentSizeLimit(업로드 실효 상한)은 legacy 이하 서버값을
// dev 확장(500MB)으로 벌려 놓아 Basic(10MB) 초과 안내 판정에 쓰면 dev 에서 안 뜬다.
// 안내는 플랜 값 그 자체를 본다. 플랜 미적용 환경(KR 등)은 null — 안내 없음.
// 이 모듈에 두는 이유: 의존성이 tier(zero)뿐이라 어떤 컴포넌트/테스트에서도
// 무거운 체인(i18n·api) 없이 import 가능 (extensionBridge 와 같은 배치 원칙).
export const getPlanFileSizeLimit = (): number | null => {
  const serverLimit = getPlanLicenseInternal()?.max_file_size_bytes
  return typeof serverLimit === 'number' && serverLimit > 0 ? serverLimit : null
}

// 저장된 첨부의 size 는 **암호문(S3 객체) 크기**이고 플랜 상한은 **평문** 기준이다.
// 그대로 비교하면 서버가 정상 통과시킨 상한 근처 파일(예: 평문 30MB-100B → 객체
// 31,457,328B > 30MiB)이 상시 "초과" 로 잡혀, 그 레코드는 빨간 안내 + 저장 차단으로
// 영구 편집 불가가 된다. 백엔드 max_object_size_for_plain_limit 과 같은 산식으로
// 허용치를 환산해 비교한다 (2026-08-25).
// 상수는 SBC1 포맷 정의 — services/attachmentV2/format.js(헤더 36, iv 12 + tag 16)와
// 백엔드 attachment_multipart_service 의 값이 같아야 하며, 아래 테스트가 수치를 고정한다.
// 이 모듈은 의존성 zero 를 유지해야 해(포맷 모듈 import 금지) 값을 복제한다.
const SBC1_HEADER_SIZE = 36
const SBC1_CHUNK_OVERHEAD = 28
const SBC1_MIN_CHUNK_SIZE = 5 * 1024 * 1024 // S3 최소 파트 크기 = 오버헤드 최대치 가정

export const maxStoredSizeForPlanLimit = (planLimit: number): number => {
  if (planLimit <= 0) return planLimit
  const chunkCount = Math.ceil(planLimit / SBC1_MIN_CHUNK_SIZE)
  return SBC1_HEADER_SIZE + chunkCount * SBC1_CHUNK_OVERHEAD + planLimit
}

// 다운그레이드로 잔존한 플랜 상한 초과 첨부 판정(§4.7).
export const hasPlanOversizedFile = (
  fileInfos: Array<{ size?: number }> | null | undefined,
): boolean => {
  const limit = getPlanFileSizeLimit()
  if (limit == null) return false
  const maxStored = maxStoredSizeForPlanLimit(limit)
  return (fileInfos || []).some((info) => typeof info?.size === 'number' && info.size > maxStored)
}

// 회사 저장 용량 총량 초과 판정(§4.7) — 다운그레이드 등으로 사용량이 상한을 넘은 상태.
// 이 상태에선 서버가 신규 업로드만 거부한다(기존 데이터 소급 차단 없음). license 는
// redux user.paidLicense 를 인자로 받는다 — planContext 는 비반응형이라 배너류 표시
// 컴포넌트가 useSelector 값으로 재렌더에 동조하기 위함. 총량 미적용 환경(KR 등)은
// storage_quota_bytes 자체가 안 내려와 quota<=0 → 항상 false.
export const isStorageOverQuota = (
  license: { storage_quota_bytes?: number; storage_used_bytes?: number } | null | undefined,
): boolean => {
  const quota = Number(license?.storage_quota_bytes) || 0
  const used = Number(license?.storage_used_bytes) || 0
  return quota > 0 && used > quota
}

// 개인당 저장 용량 초과 판정 — B축(유저 플랜) PRO 전용 (기획 확정 2026-08-25 "1인당 2GB").
// personal_storage_* 는 그 경우에만 내려오고 회사 풀 필드(storage_quota_bytes)와 상호
// 배타라, 두 헬퍼가 동시에 true 가 되는 일은 없다. used 는 요청자 본인의 사용량.
export const isPersonalStorageOverQuota = (
  license:
    | { personal_storage_quota_bytes?: number; personal_storage_used_bytes?: number }
    | null
    | undefined,
): boolean => {
  const quota = Number(license?.personal_storage_quota_bytes) || 0
  const used = Number(license?.personal_storage_used_bytes) || 0
  return quota > 0 && used > quota
}

// 사이드바 회사 풀 저장용량 게이지 노출 판정 (사용자 확정 2026-08-25).
//
// 🛑 A축 PRO 분기는 이제 **구버전 백엔드 호환 가드**다 (2026-08-27). A축 PRO 는 회사 총량이
// 없다고 정정돼 서버가 storage_quota_bytes 를 null 로 내리므로, 새 백엔드와 짝일 때는 첫
// 가드(quota <= 0)에서 이미 false 가 된다. 그럼에도 분기를 남기는 이유는 **프론트가 백엔드보다
// 먼저 뜬 구간**(dev 는 두 배포가 동시 트리거)에 구버전 응답이 회사 풀(계약 수 × 300MB)을
// 실어 보내면 사이드바에 체감 불가한 수치(예: 111건 = 32.5GB)가 뜨기 때문이다.
// 백엔드가 전 환경에 배포된 뒤 제거 가능.
// 나머지 회사 풀(양 축 Basic = 회사 10GB 공용)은 상한이 작아 실제로 닿을 수 있어 계속 보여준다.
// B축 PRO(개인 2GB)는 애초에 personal_storage_* 경로라 이 판정 밖.
//
// 초과 상단 배너(StorageOverQuotaNotice)는 이 판정과 무관하게 유지한다 — 게이지는
// 상시 정보라 숨기지만, 업로드가 막히는 이유 설명은 A축 PRO 에서도 필요하다.
//
// PRO 판정을 hasProFeature()(planContext) 대신 license 인자로 재구현하는 이유: ① 표시
// 컴포넌트는 useSelector(paidLicense) 재렌더에 동조해야 하고(planContext 는 비반응형)
// ② hasProFeature 의 fail-open 분기(한국향·컨텍스트 미주입 = true)는 "기능 허용" 기준이라
// 표시 판정에 그대로 쓰면 의미가 어긋난다. 회사 풀 필드 자체가 글로벌향+flag ON 에서만
// 내려오므로 여기서는 글로벌향 전제 규칙(TRIAL 또는 PROFESSIONAL = PRO)만 보면 충분하다.
export const isCompanyStorageGaugeVisible = (
  license:
    | {
        storage_quota_bytes?: number
        storage_used_bytes?: number
        quota_mode?: string
        plan_type?: string
        plan_tier?: string
      }
    | null
    | undefined,
): boolean => {
  const quota = Number(license?.storage_quota_bytes) || 0
  if (quota <= 0 || license?.storage_used_bytes == null) return false

  const isProEquivalent =
    license?.plan_type === PLAN_TYPE.TRIAL || license?.plan_tier === PLAN_TIER_PROFESSIONAL
  return !(license?.quota_mode === QUOTA_MODE.RESOURCE && isProEquivalent)
}

// ─── A축(리소스 플랜) PRO: 레코드 1건당 저장용량 (기획 확정 2026-08-27) ───
//
// 서버 산출값(license.per_resource_storage_quota_bytes)이 정본이다 — 프론트는 산식을
// 재구현하지 않는다. 특약 override 도 서버에서 이미 반영돼 내려온다.
// 적용되지 않는 환경(KR·Basic·B축 PRO·구버전 백엔드)은 필드가 없어 null → 안내 없음.
export const getPlanPerResourceLimit = (): number | null => {
  const serverLimit = getPlanLicenseInternal()?.per_resource_storage_quota_bytes
  return typeof serverLimit === 'number' && serverLimit > 0 ? serverLimit : null
}

// 저장된 첨부 합계로 "이 레코드가 레코드당 상한을 넘었나" 판정 (다운그레이드 잔존 안내용).
//
// getPlanFileSizeLimit 과 같은 평문/암호문 문제가 있다 — 저장된 size 는 암호문(S3 객체)
// 크기이고 상한은 평문 기준이라, 그대로 비교하면 서버가 통과시킨 레코드가 화면에서만
// "초과" 로 잡혀 영구 편집 불가가 된다(2026-08-25 사고와 같은 계열).
// 여기서는 파일이 여러 개라 오버헤드가 **파일 수만큼** 붙으므로 단일 파일 산식
// (maxStoredSizeForPlanLimit)보다 넉넉한 허용치가 필요하다. 안내는 거짓 양성이 더
// 해로우므로(멀쩡한 레코드에 빨간 배너) **관대한 쪽으로** 잡는다 — 진짜 차단은 서버(495)다.
export const maxStoredSizeForRecordTotal = (planLimit: number, fileCount: number): number => {
  if (planLimit <= 0) return planLimit
  const files = Math.max(1, fileCount)
  // 파일마다 헤더 1개 + 최소 청크 1개, 여기에 총량이 5MB 단위로 쪼개지는 만큼 추가.
  const chunks = Math.ceil(planLimit / SBC1_MIN_CHUNK_SIZE) + files
  return SBC1_HEADER_SIZE * files + chunks * SBC1_CHUNK_OVERHEAD + planLimit
}

export const isRecordStorageOverQuota = (
  items: Array<{ type?: string; content?: Array<{ size?: number }> }> | null | undefined,
): boolean => {
  const limit = getPlanPerResourceLimit()
  if (limit == null) return false
  const sizes = (items || [])
    .filter((item) => item?.type === 'files')
    .flatMap((item) => item.content || [])
    .map((info) => (typeof info?.size === 'number' ? info.size : 0))
  if (!sizes.length) return false
  const total = sizes.reduce((sum, size) => sum + size, 0)
  return total > maxStoredSizeForRecordTotal(limit, sizes.length)
}

// 레코드 items 배열 단위 판정 — 상세 카드 상단(제목 위) 빨간 안내용.
export const hasPlanOversizedFileInItems = (
  items: Array<{ type?: string; content?: Array<{ size?: number }> }> | null | undefined,
): boolean =>
  (items || []).some((item) => item?.type === 'files' && hasPlanOversizedFile(item.content))

export { hasProFeature }
export { setPlanContext, getPlanLicense } from 'utils/plan/tier'
